/* eslint-disable no-labels */
import { ElementDataView } from "../../model/elements/SDKElementDataView";
import { ElementPropertyTargetElementType, } from "../../model/elements/SDKElementProperty";
import { ALL_TOKEN_TYPES, TokenType } from "../../model/enums/SDKTokenType";
import { ThemeUtilities } from "../../model/themes/SDKThemeUtilities";
import { TokenUtils } from "../../utils/TokenUtils";
import { AreaTokenValues } from "./SDKAreaTokenValues";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tokens Area
export class AreaTokens {
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(dataCore) {
        /** Update helpers for everything token related */
        this.values = new AreaTokenValues();
        this.dataCore = dataCore;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Read
    /** Fetches all remote tokens in the version
     * @param from - Remote version to fetch from
     * @param filter - Optional filter to apply. Multiple filters can be used at once
     * @param filter.type - When provided, only tokens of the specified type will be returned
     * @param filter.brandId - When provided, only tokens of the specified brand will be returned
     * @returns Tokens in the specified version with filter applied
     */
    async getTokens(from, filter) {
        return this.dataCore.tokens(from.designSystemId, from.versionId, filter);
    }
    /** Retrieves all occurances of the specified token in the version - its usages in other tokens, components and documentation pages
     * @param from - Remote version to fetch from
     * @param idInVersion - Token to check for references
     * @returns Persistent identifiers of tokens, components, documentation pages that contain the specified token
     */
    async getTokenUsage(from, idInVersion) {
        return this.dataCore.getTokenUsage(from.designSystemId, from.versionId, idInVersion);
    }
    /** Fetches all remote token groups in this version
     * @param from - Remote version to fetch from
     * @param filter - Optional filter to apply. Multiple filters can be used at once
     * @param filter.type - When provided, only token groups of the specified type will be returned
     * @param filter.brandId - When provided, only token groups of the specified brand will be returned
     * @returns Token groups in the specified version with filter applied
     */
    async getTokenGroups(from, filter) {
        return this.dataCore.tokenGroups(from.designSystemId, from.versionId, filter);
    }
    /** Fetches all remote themes in the version
     * @param from - Remote version to fetch from
     * @returns All themes in the specified version
     */
    async getTokenThemes(from, filter) {
        return this.dataCore.themes(from.designSystemId, from.versionId, filter);
    }
    /** Retrieves all remote property definitions for tokens
     * @param from - Remote version to fetch from
     * @returns All property definitions in the specified version
     */
    async getTokenProperties(from) {
        return this.dataCore.elementPropertyDefinitions(from.designSystemId, from.versionId, ElementPropertyTargetElementType.token);
    }
    /** Retrieves all remote property definitions for tokens
     * @param from - Remote version to fetch from
     * @returns All property definitions in the specified version
     */
    async getTokenPropertyValues(from) {
        return this.dataCore.elementPropertyValues(from.designSystemId, from.versionId);
    }
    /** Retrieves all remote property views for tokens
     * @param from - Remote version to fetch from
     * @returns All property views in the specified version
     */
    async getTokenDataViews(from) {
        return this.dataCore.elementViews(from.designSystemId, from.versionId, ElementPropertyTargetElementType.token);
    }
    /** Retrieves all token collections
     * @param from - Remote version to fetch from
     * @returns All token collections in the specified version
     */
    async getTokenCollections(from) {
        return this.dataCore.tokenCollections(from.designSystemId, from.versionId);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Create/Update
    /** Creates remote token. This method can only be used once per token and will assign persistent id to the token. Calling this version on token that already has id assigned will result in error
     * @param to - Remote version to write to
     * @param token - Token to create
     * @param parentId - Id of the group to add the token to. This is id of the group, not the versioned id
     * @returns New remote token ids
     */
    async createToken(to, token, parentId) {
        return this.dataCore.createToken(to.designSystemId, to.versionId, token, parentId);
    }
    /** Creates local token. This token doesn't have id associated with it on remote, and must be created once with `createToken` to be stored in design system
     * @param tokenType - Type of token to create
     * @returns New token of the specified type, filled with default values
     */
    createLocalToken(tokenType, versionId, brandId, properties) {
        return TokenUtils.createDefaultToken(tokenType, versionId, brandId, properties);
    }
    /** Creates local token override. This theme override doesn't have id associated with it on remote, and must be created once with `createThemeOverride` to be stored in design system
     * @param tokenType - Type of token to create
     * @returns New token of the specified type, filled with default values
     */
    createLocalThemeOverride(theme, baseToken) {
        const replica = ThemeUtilities.replicateTokenWithValue(baseToken);
        replica.propertyValues = {};
        replica.themeId = theme.id;
        replica.origin = null;
        return replica;
    }
    /** Creates remote token group. This method can only be used once per group and will assign persistent id to the group. Calling this version on group that already has id assigned will result in error
     * @param to - Remote version to write to
     * @param group - Group to create
     * @param parentId - Id of the group to add the group to. This is id of the group, not the versioned id
     * @returns New remote token group ids
     */
    async createTokenGroup(to, group, parentId) {
        return this.dataCore.createTokenGroup(to.designSystemId, to.versionId, group, parentId);
    }
    /** Creates local token group. This group doesn't have id associated with it on remote, and must be created once with `createTokenGroup` to be stored in design system
     * @param tokenType - Type of token to create the group for
     * @param versionId - Id of the version to create the group in
     * @param brandId - Id of the brand to create the group in
     * @returns New group with id assigned
     */
    createLocalTokenGroup(tokenType, versionId, brandId) {
        return TokenUtils.createDefaultTokenGroup(tokenType, versionId, brandId);
    }
    /** Updates remote token. This method can only be used on tokens that already exist in the version
     * @param to - Remote version to write to
     * @param token - Token to update
     * @returns Nothing
     */
    async updateToken(to, token) {
        return this.dataCore.updateToken(to.designSystemId, to.versionId, token);
    }
    /** Updates remote token groups. This method can only be used on token groups that already exist in the version
     * @param to - Remote version to write to
     * @param group - Token group to update
     * @returns Nothing
     */
    async updateTokenGroup(to, group) {
        return this.dataCore.updateTokenGroup(to.designSystemId, to.versionId, group);
    }
    /** Updates remote token property value. This also creates the property value if it doesn't exist
     * @param to - Remote version to write to
     * @param newValue - Value to set - this can be multiple things, from ID to raw string value to others
     * @param forToken - Token to assign the value to
     * @param forProperty - Property to assign the value to
     */
    async updateTokenPropertyValue(to, newValue, forToken, forProperty) {
        return this.dataCore.updateTokenPropertyValue(to.designSystemId, to.versionId, newValue, forToken, forProperty);
    }
    /** Deletes remote token property value
     * @param to - Remote version to write to
     * @param valueId - Value identifier
     */
    async deleteTokenPropertyValue(to, valueId) {
        return this.dataCore.deleteTokenPropertyValue(to.designSystemId, to.versionId, valueId);
    }
    /** Create token property
     * @param to - Remote version to write to
     * @param model - Property creation model to create the property from
     */
    async createTokenProperty(to, model) {
        return this.dataCore.createTokenProperty(to.designSystemId, to.versionId, model);
    }
    /** Update token property
     * @param to - Remote version to write to
     * @param propertyIdInVersion - Remote element property to update
     * @param model - Property update model to apply to property
     */
    async updateTokenProperty(to, propertyIdInVersion, model) {
        return this.dataCore.updateTokenProperty(to.designSystemId, to.versionId, propertyIdInVersion, model);
    }
    /** Delete token property. This will remove all values associated with the property
     * @param to - Remote version to write to
     * @param propertyIdInVersion - Id of the property to delete
     */
    async deleteTokenProperty(to, propertyIdInVersion) {
        return this.dataCore.deleteTokenProperty(to.designSystemId, to.versionId, propertyIdInVersion);
    }
    /** Resize specific token column
     * @param to - Remote version to write to
     * @param columnIdInVersion - Id of the column to update
     * @param newWidth - New width of the column
     */
    async updateResizeTokenColumn(to, view, column, newWidth) {
        return this.dataCore.updateResizeTokenColumn(to.designSystemId, to.versionId, view, column, newWidth);
    }
    /** Move column to a new index and reorder all other columns around it
     * @param to - Remote version to write to
     * @param colum - Column object to move
     * @param newIndex - New index to move the token to
     */
    async updateReorderTokenColumn(to, view, column, newIndex) {
        return this.dataCore.updateReorderTokenColumn(to.designSystemId, to.versionId, view, column, newIndex);
    }
    /** Creates local token theme. This group doesn't have id associated with it on remote, and must be created once with `createTokenTheme` to be stored in design system
     * @param versionId - Id of the version to create the theme in
     * @param brandId - Id of the brand to create the theme in
     * @returns New group with id assigned
     */
    createLocalTokenTheme(versionId, brandId) {
        return TokenUtils.createDefaultTokenTheme(versionId, brandId);
    }
    /** Creates remote token theme. This method can only be used once per theme and will assign persistent id to the theme. Calling this version on theme that already has id assigned will result in error
     * @param to - Remote version to write to
     * @param theme - Theme to create
     * @returns New remote theme ids
     */
    async createTokenTheme(to, theme) {
        return this.dataCore.createTokenTheme(to.designSystemId, to.versionId, theme);
    }
    /** Updates token theme. This method can only be used on themes that already exist in the version
     * @param to - Remote version to write to
     * @param theme - Token theme to update
     * @returns Nothing
     */
    async updateTokenTheme(to, theme) {
        return this.dataCore.updateTokenTheme(to.designSystemId, to.versionId, theme);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Delete
    /** Deletes remote token. Calling this method on token that doesn't exist in the version will result in error
     * @param to - Remote version to write to
     * @param tokenIdInVersion - Token idInVersion to delete
     * @returns Nothing
     */
    async deleteToken(to, tokenIdInVersion) {
        return this.dataCore.deleteToken(to.designSystemId, to.versionId, tokenIdInVersion);
    }
    /** Deletes remote token group. This method will also fix the token group tree by moving any subgroups to parent - do note it will NOT delete contents of the group. Calling this method on group that doesn't exist in the version will result in error
     * @param to - Remote version to write to
     * @param tokenGroupIdInVersion - Token group idInVersion to delete
     * @returns Nothing
     */
    async ungroupTokenGroup(to, tokenGroupIdInVersion) {
        return this.dataCore.ungroupTokenGroup(to.designSystemId, to.versionId, tokenGroupIdInVersion);
    }
    /** Deletes remote token group and all its descendants. All items (groups and tokens) underneath will be deleted. Calling this method on group that doesn't exist in the version will result in error
     * @param to - Remote version to write to
     * @param tokenGroupToDelete - Token group to delete
     * @param tokens - All available tokens
     * @param tokenGroups - All available token groups
     * @returns Nothing
     */
    async deleteTokenGroup(to, tokenGroupToDelete, tokens, tokenGroups) {
        return this.dataCore.deleteTokenGroup(to.designSystemId, to.versionId, tokenGroupToDelete, tokens, tokenGroups);
    }
    /** Deletes remote token theme. This method will also remove all theme overrides and disconnect theme from assigned column, all of which will be updated
     * @param to - Remote version to write to
     * @param idInVersion - Theme idInVersion to delete
     * @returns Nothing
     */
    async deleteTokenTheme(to, idInVersion) {
        return this.dataCore.deleteTokenTheme(to.designSystemId, to.versionId, idInVersion);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Bulk editing
    /** Warning: INTERNAL, DO NOT USE YET, NOT STABLE INTERFACE. Write tokens in bulk */
    async writeTokens(to, tokens, groups, deleteTokens, deleteTokenGroups) {
        return this.dataCore.writeTokenData(to.designSystemId, to.versionId, tokens, groups, deleteTokens, deleteTokenGroups);
    }
    /** Warning: INTERNAL, DO NOT USE YET, NOT STABLE INTERFACE. Write theme in bulk */
    async writeTheme(to, theme) {
        return this.dataCore.writeTokenThemeData(to.designSystemId, to.versionId, theme);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Computing
    /** Resolves tokens by applying themes to them and retrieves new token set where values correctly correspond to that theme */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    computeTokensByApplyingThemes(allTokens, tokens, themes) {
        // Create hashes index of all tokens
        const tokensIndex = new Map();
        for (const token of allTokens) {
            tokensIndex.set(token.id, ThemeUtilities.replicateTokenWithValue(token));
        }
        // Create (crude) hashed search index that contains mapped themes <> tokens to make the lookup more performant
        const themeOverridesIndex = {};
        for (const theme of themes) {
            const overrides = new Map();
            for (const override of theme.overriddenTokens) {
                overrides.set(override.id, ThemeUtilities.replicateTokenWithValue(override));
            }
            themeOverridesIndex[theme.id] = overrides;
        }
        // Calculate token overrides
        const resolvedTopLevelTokens = this.computeTokensByApplyingThemesEfficient(tokensIndex, themeOverridesIndex, tokens, themes);
        // At this point we have all tokens resolved from top level but some tokens also have references to other tokens (ie. typography -> font size etc.), so now the resolver will continue replacing those
        const resolvedFixedReferencedTokens = this.computeInnerReferencesByApplyingThemesEfficient(tokensIndex, themeOverridesIndex, resolvedTopLevelTokens, themes);
        return resolvedFixedReferencedTokens;
    }
    computeInnerReferencesByApplyingThemesEfficient(tokensIndex, themeOverridesIndex, tokens, themes) {
        // For every token check if it is a complex token, then resolve each of its sub-values separately
        const resolvedTokens = [];
        for (const token of tokens) {
            if (token.tokenType === TokenType.typography) {
                const t = token;
                t.value = {
                    fontFamily: t.value.fontFamily.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.fontFamily.referencedTokenId, themes)
                        : t.value.fontFamily,
                    fontWeight: t.value.fontWeight.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.fontWeight.referencedTokenId, themes)
                        : t.value.fontWeight,
                    fontSize: t.value.fontSize.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.fontSize.referencedTokenId, themes)
                        : t.value.fontSize,
                    textDecoration: t.value.textDecoration.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.textDecoration.referencedTokenId, themes)
                        : t.value.textDecoration,
                    textCase: t.value.textCase.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.textCase.referencedTokenId, themes)
                        : t.value.textCase,
                    letterSpacing: t.value.letterSpacing.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.letterSpacing.referencedTokenId, themes)
                        : t.value.letterSpacing,
                    lineHeight: t.value.lineHeight
                        ? t.value.lineHeight.referencedTokenId
                            ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                                this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.lineHeight.referencedTokenId, themes)
                            : t.value.lineHeight
                        : null,
                    paragraphIndent: t.value.paragraphIndent.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.paragraphIndent.referencedTokenId, themes)
                        : t.value.paragraphIndent,
                    paragraphSpacing: t.value.paragraphSpacing.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.paragraphSpacing.referencedTokenId, themes)
                        : t.value.paragraphSpacing,
                    referencedTokenId: t.value.referencedTokenId,
                };
                resolvedTokens.push(t);
            }
            else if (token.tokenType === TokenType.border) {
                const t = token;
                t.value = {
                    width: t.value.width.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.width.referencedTokenId, themes)
                        : t.value.width,
                    color: t.value.color.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.color.referencedTokenId, themes)
                        : t.value.color,
                    style: t.value.style,
                    position: t.value.position,
                    referencedTokenId: t.value.referencedTokenId,
                };
                // Note: Second pass, resolve only opacity if exists
                if (t.value.color.opacity.referencedTokenId) {
                    t.value = Object.assign(Object.assign({}, t.value), { color: Object.assign(Object.assign({}, t.value.color), { opacity: this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.color.opacity.referencedTokenId, themes) }) });
                }
                resolvedTokens.push(t);
            }
            else if (token.tokenType === TokenType.color) {
                const t = token;
                t.value = {
                    color: t.value.color,
                    opacity: t.value.opacity.referencedTokenId
                        ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, t.value.opacity.referencedTokenId, themes)
                        : t.value.opacity,
                    referencedTokenId: t.value.referencedTokenId,
                };
                resolvedTokens.push(t);
            }
            else if (token.tokenType === TokenType.shadow) {
                const t = token;
                t.value = t.value.map((layer) => {
                    let value = {
                        color: layer.color.referencedTokenId
                            ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, layer.color.referencedTokenId, themes)
                            : layer.color,
                        x: layer.x,
                        y: layer.y,
                        radius: layer.radius,
                        spread: layer.spread,
                        opacity: layer.opacity && layer.opacity.referencedTokenId
                            ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                                this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, layer.opacity.referencedTokenId, themes)
                            : layer.opacity,
                        type: layer.type,
                        referencedTokenId: layer.referencedTokenId,
                    };
                    // Note: Second pass, resolve only opacity if exists
                    if (value.color.opacity.referencedTokenId) {
                        value = Object.assign(Object.assign({}, value), { color: Object.assign(Object.assign({}, value.color), { opacity: this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, value.color.opacity.referencedTokenId, themes) }) });
                    }
                    return value;
                });
                resolvedTokens.push(t);
            }
            else if (token.tokenType === TokenType.gradient) {
                const t = token;
                t.value = t.value.map((layer) => {
                    const value = {
                        to: layer.to,
                        from: layer.from,
                        aspectRatio: layer.aspectRatio,
                        stops: layer.stops.map((stop) => {
                            return {
                                color: stop.color.referencedTokenId
                                    ? this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, stop.color.referencedTokenId, themes)
                                    : stop.color,
                                position: stop.position,
                            };
                        }),
                        type: layer.type,
                        referencedTokenId: layer.referencedTokenId,
                    };
                    // Note: Second pass, resolve only opacity if exists
                    value.stops = value.stops.map((stop) => {
                        if (stop.color.opacity.referencedTokenId) {
                            return Object.assign(Object.assign({}, stop), { color: Object.assign(Object.assign({}, stop.color), { opacity: this.computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, stop.color.opacity.referencedTokenId, themes) }) });
                        }
                        return stop;
                    });
                    return value;
                });
                resolvedTokens.push(t);
            }
            else {
                resolvedTokens.push(token);
            }
        }
        return resolvedTokens;
    }
    computeSingleTokenValueFromReferenceByApplyingThemes(tokensIndex, themeOverridesIndex, token, themes) {
        const resolvedTokens = this.computeTokensByApplyingThemesEfficient(tokensIndex, themeOverridesIndex, [tokensIndex.get(token)], themes);
        if (resolvedTokens.length !== 1) {
            throw new Error(`Expected to resolve a single token but resolved ${resolvedTokens.length} tokens instead`);
        }
        const resolvedToken = resolvedTokens[0];
        // If the resolved token is not an array, we need to add reference to it
        if (!Array.isArray(resolvedToken.value)) {
            return Object.assign(Object.assign({}, resolvedToken.value), { referencedTokenId: resolvedToken.id });
        }
        return resolvedToken.value;
    }
    computeTokensByApplyingThemesEfficient(tokensIndex, themeOverridesIndex, tokens, themes) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        // Select each correct token by first searching the overrides in reverse order, or fallback to default token if not found in any
        const reversedThemes = [...themes].reverse();
        const resolvedTokens = [];
        let processedThemes = [];
        for (const token of tokens) {
            processedThemes = [];
            let override = null;
            const resolvedInitialCount = resolvedTokens.length;
            /**
             * The following part handles the case where token value is pure
             */
            coreThemeLoop: for (const theme of reversedThemes) {
                processedThemes.push(theme);
                override = (_b = (_a = themeOverridesIndex[theme.id]) === null || _a === void 0 ? void 0 : _a.get(token.id)) !== null && _b !== void 0 ? _b : null;
                if (override) {
                    // If there is override, prioritize that
                    // If the override is not a reference, just use it as is
                    let possiblyReferencedValue = override.value;
                    if (possiblyReferencedValue instanceof Array) {
                        // In case value is array, just use its raw value and don't check for further references
                        resolvedTokens.push(override);
                        break;
                    }
                    let reference = possiblyReferencedValue.referencedTokenId;
                    if (reference) {
                        // If the token is reference, we need to check if the reference has override in any of the themes we have processed until this point
                        let parentToken = tokensIndex.get(reference);
                        while (parentToken) {
                            // Token is referenced, check if that reference has override in any of the themes we have processed until this point
                            for (const processedTheme of processedThemes) {
                                override =
                                    (_d = (_c = themeOverridesIndex[processedTheme.id]) === null || _c === void 0 ? void 0 : _c.get(parentToken.id)) !== null && _d !== void 0 ? _d : null;
                                if (override) {
                                    resolvedTokens.push(override);
                                    break coreThemeLoop;
                                }
                            }
                            // Do the same reference check for theme-related overrides
                            override =
                                (_f = (_e = themeOverridesIndex[theme.id]) === null || _e === void 0 ? void 0 : _e.get(parentToken.id)) !== null && _f !== void 0 ? _f : null;
                            if (override) {
                                resolvedTokens.push(override);
                                break coreThemeLoop;
                            }
                            possiblyReferencedValue = parentToken.value;
                            if (possiblyReferencedValue instanceof Array) {
                                // This will not happen because any array value was already returned above but for type purposes we keep it
                                break;
                            }
                            reference = possiblyReferencedValue.referencedTokenId;
                            if (reference) {
                                // Move on to check another level of reference if present
                                parentToken = tokensIndex.get(reference);
                            }
                            else {
                                resolvedTokens.push(parentToken);
                                break coreThemeLoop;
                            }
                        }
                    }
                    else {
                        // No reference, just use the override as is
                        resolvedTokens.push(override);
                        break;
                    }
                }
            }
            override = null;
            if (resolvedInitialCount !== resolvedTokens.length) {
                // Skip the next part if we have already resolved the token
                continue;
            }
            /**
             * The following part handles the case where token value is a reference
             */
            coreThemeLoop: for (const theme of reversedThemes) {
                processedThemes.push(theme);
                if (!override) {
                    // Check if token has reference and if it has, check if the reference has override in any of the themes that are going to be applied, recursively
                    let possiblyReferencedValue = token.value;
                    if (possiblyReferencedValue instanceof Array) {
                        // This will not happen because any array value was already returned above but for type purposes we keep it
                        break;
                    }
                    let reference = possiblyReferencedValue.referencedTokenId;
                    if (reference) {
                        let parentToken = tokensIndex.get(reference);
                        while (parentToken) {
                            for (const processedTheme of processedThemes) {
                                override =
                                    (_h = (_g = themeOverridesIndex[processedTheme.id]) === null || _g === void 0 ? void 0 : _g.get(parentToken.id)) !== null && _h !== void 0 ? _h : null;
                                if (override) {
                                    resolvedTokens.push(override);
                                    break coreThemeLoop;
                                }
                            }
                            override =
                                (_k = (_j = themeOverridesIndex[theme.id]) === null || _j === void 0 ? void 0 : _j.get(parentToken.id)) !== null && _k !== void 0 ? _k : null;
                            if (override) {
                                resolvedTokens.push(override);
                                break coreThemeLoop;
                            }
                            possiblyReferencedValue = parentToken.value;
                            if (possiblyReferencedValue instanceof Array) {
                                // This will not happen because any array value was already returned above but for type purposes we keep it
                                override = null;
                                break coreThemeLoop;
                            }
                            reference = possiblyReferencedValue.referencedTokenId;
                            if (reference) {
                                parentToken = tokensIndex.get(reference);
                            }
                            else {
                                parentToken = undefined;
                            }
                        }
                    }
                }
            }
            /**
             * There was no resolution to theme, so we return the token as is
             */
            if (resolvedInitialCount === resolvedTokens.length) {
                resolvedTokens.push(token);
            }
            /** Validate that we have exactly a single token resolved per run */
            if (resolvedInitialCount + 1 !== resolvedTokens.length) {
                throw new Error(`Token can only resolve into a single entity again but resolved into ${resolvedInitialCount - resolvedTokens.length} entities instead - resolved before: ${resolvedInitialCount}, in this pass: ${resolvedTokens.length}`);
            }
        }
        // Verify that all tokens are resolved
        if (resolvedTokens.length !== tokens.length) {
            throw new Error(`Not all tokens were resolved by applying themes. Resolved: ${resolvedTokens.length}, Expected: ${tokens.length}`);
        }
        // Create an array of original tokens with new values overriden by the resolution
        const finalTokens = [];
        tokens.forEach((t, index) => {
            const anyToken = t;
            // Any types are necessary right now as we can't reliably type this later, to improve eventually
            const tokenWithoutValue = ThemeUtilities.replicateTokenWithoutValue(anyToken);
            const resolvedToken = resolvedTokens[index];
            let resolvedValue = resolvedToken === null || resolvedToken === void 0 ? void 0 : resolvedToken.value;
            if (resolvedToken &&
                resolvedValue &&
                !Array.isArray(resolvedValue) &&
                // add reference to the resolved token if it's not the same as the original token
                resolvedToken.id !== anyToken.id) {
                // add reference to the resolved token
                resolvedValue = Object.assign(Object.assign({}, resolvedValue), { referencedTokenId: resolvedToken.id });
            }
            tokenWithoutValue.value = resolvedValue !== null && resolvedValue !== void 0 ? resolvedValue : anyToken.value;
            finalTokens.push(tokenWithoutValue);
        });
        return finalTokens;
    }
    /** Separates groups by types */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async computeGroupTrees(groups) {
        const rootGroups = groups.filter((g) => g.isRoot);
        const trees = new Map();
        for (const group of rootGroups) {
            const branch = trees.get(group.tokenType);
            if (branch) {
                branch.push(group);
                trees.set(group.tokenType, branch);
            }
            else {
                trees.set(group.tokenType, [group]);
            }
        }
        return trees;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Mock Data
    /** Get determistic mocked token data. Must be used with mocked token groups */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async getMockedTokens(from) {
        const tokens = [];
        ALL_TOKEN_TYPES.forEach((type) => {
            const token = this.createLocalToken(type, from.versionId, from.brandId, []);
            // Modify id for determinism
            token.id = `mocked-token-${type}`;
            tokens.push(token);
        });
        return tokens;
    }
    /** Get determistic mocked token group data. Must be used with mocked tokens */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async getMockedTokenGroups(from) {
        const rootGroups = [];
        ALL_TOKEN_TYPES.forEach((type) => {
            const group = this.createLocalTokenGroup(type, from.versionId, from.brandId);
            // Modify id for determinism, and mark all as root groups
            group.id = `mocked-group-${type}`;
            group.childrenIds = [`mocked-token-${type}`];
            group.isRoot = true;
            rootGroups.push(group);
        });
        return rootGroups;
    }
    /** Get determistic mocked token data views. Must be used with mocked tokens */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async getMockedTokenDataViews(
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    from) {
        return [
            new ElementDataView({
                id: "mocked-token-data-view",
                persistentId: "mocked-token-data-view-persistent-id",
                isDefault: true,
                targetElementType: ElementPropertyTargetElementType.token,
                columns: [],
                meta: {
                    name: "Mocked token data view",
                    description: "",
                },
            }),
        ];
    }
}
